/**
 * The **material ladder** (PRD #795, design pinned by #713).
 *
 * Nine tiers, declared once, each carrying a multiplier and a hue. The tier is
 * a pure multiplier on a base's implicit stat and nothing else: it never
 * touches an affix, it never changes a slot, and it never changes what family
 * an item belongs to, so leather stays fast and plate stays armoured at every
 * rung of the ladder.
 *
 * The ladder is affordable because britbank already renders per-layer partial
 * hue dyeing. A tier is a hue applied to art that already exists, which is why
 * nine tiers across every family costs no art budget at all. If that assumption
 * ever breaks, the ladder shrinks rather than growing an art budget.
 *
 * The multipliers are **first-pass**. They are declared here rather than at
 * call sites so a balance change has one place to happen, and they are dialed
 * by the tuning PRD (#799). Do not defend these values; move them.
 */

/** Every material tier, shallowest to deepest. Order is the ladder. */
export const ARPG_MATERIAL_TIERS = [
  'normal',
  'dull-copper',
  'shadow',
  'copper',
  'bronze',
  'gold',
  'agapite',
  'verite',
  'valorite',
] as const;

export type ArpgMaterialTier = (typeof ARPG_MATERIAL_TIERS)[number];

export interface ArpgMaterialDefinition {
  id: ArpgMaterialTier;
  /** Player-facing name, the first word of a rolled item's name. */
  name: string;
  /** One-based rung on the ladder, which is what a drop window is expressed in. */
  rank: number;
  /** What this tier multiplies a base's implicit stat by. Never an affix. */
  multiplier: number;
  /**
   * The UO hue the equipped layer is dyed with, or zero for raw art.
   *
   * These are the ore hues the shard has always used for the same eight metals,
   * so a Valorite chest reads as Valorite to anyone who has seen one before.
   */
  hue: number;
}

const MATERIAL_DEFINITIONS: readonly ArpgMaterialDefinition[] = [
  { id: 'normal', name: 'Normal', rank: 1, multiplier: 1, hue: 0 },
  { id: 'dull-copper', name: 'Dull Copper', rank: 2, multiplier: 1.2, hue: 2419 },
  { id: 'shadow', name: 'Shadow', rank: 3, multiplier: 1.4, hue: 2406 },
  { id: 'copper', name: 'Copper', rank: 4, multiplier: 1.6, hue: 2413 },
  { id: 'bronze', name: 'Bronze', rank: 5, multiplier: 1.8, hue: 2418 },
  { id: 'gold', name: 'Gold', rank: 6, multiplier: 2, hue: 2213 },
  { id: 'agapite', name: 'Agapite', rank: 7, multiplier: 2.2, hue: 2425 },
  { id: 'verite', name: 'Verite', rank: 8, multiplier: 2.4, hue: 2207 },
  { id: 'valorite', name: 'Valorite', rank: 9, multiplier: 2.6, hue: 2219 },
];

const BY_ID = new Map<string, ArpgMaterialDefinition>(
  MATERIAL_DEFINITIONS.map((definition) => [definition.id, definition]),
);

const BY_RANK = new Map<number, ArpgMaterialDefinition>(
  MATERIAL_DEFINITIONS.map((definition) => [definition.rank, definition]),
);

/** The deepest rung the ladder holds. A drop window is clamped to it. */
export const ARPG_MATERIAL_TIER_COUNT = MATERIAL_DEFINITIONS.length;

export function isArpgMaterialTier(value: unknown): value is ArpgMaterialTier {
  return typeof value === 'string' && BY_ID.has(value);
}

export function arpgMaterial(tier: ArpgMaterialTier): ArpgMaterialDefinition {
  const definition = BY_ID.get(tier);
  if (!definition) throw new Error(`unknown ARPG material tier '${tier}'`);
  return definition;
}

/** The tier at a one-based rung, clamped into the ladder at both ends. */
export function arpgMaterialAtRank(rank: number): ArpgMaterialDefinition {
  const bounded = Math.min(
    ARPG_MATERIAL_TIER_COUNT,
    Math.max(1, Number.isFinite(rank) ? Math.round(rank) : 1),
  );
  return BY_RANK.get(bounded)!;
}

/**
 * The implicit stat this material carries a base to.
 *
 * Rounded, because an implicit is a whole number a player reads off a tooltip
 * and compares. The rounding happens here rather than at the call site so a
 * tooltip and the derivation fold can never disagree about the number.
 */
export function arpgMaterialImplicit(
  tier: ArpgMaterialTier,
  baseImplicit: number,
): number {
  const value = Number.isFinite(baseImplicit) ? baseImplicit : 0;
  return Math.round(value * arpgMaterial(tier).multiplier);
}
